'use client';

import * as React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export interface JoinWaitlistButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
}

const JoinWaitlistButton = React.forwardRef<
  HTMLButtonElement,
  JoinWaitlistButtonProps
>(({ className, children, type = 'button', ...props }, ref) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const leftBarVariants = {
    initial: {
      x: 0,
      // opacity: 1,
      transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
    },
    animate: {
      x: '-100%',
      // opacity: 0,
      transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
    },
  };

  const textVariants = {
    initial: {
      x: 0,
      transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
    },
    animate: {
      x: '-10rem',
      transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
    },
  };

  const rightBarVariants = {
    initial: {
      // scaleX: 0,
      // opacity: 0,
      x: '100%',
      transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
    },
    animate: {
      scaleX: 1,
      x: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
    },
  };

  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'relative w-[31.5rem] overflow-clip !py-[2rem] text-white font-inter-tight font-[500]  flex items-center gap-[0.5rem]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'transition-colors',
        className,
      )}
      aria-label={props['aria-label'] || 'Join the release waitlist'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => setIsHovered(false)}
      {...props}
    >
      <motion.span
        className="block w-[9.7rem] h-[0.125rem] bg-offwhite flex-shrink-0"
        aria-hidden="true"
        variants={leftBarVariants}
        initial="initial"
        animate={isHovered && !props.disabled ? 'animate' : 'initial'}
      />

      <motion.span
        style={{ textWrap: 'nowrap' }}
        className="block"
        variants={textVariants}
        initial="initial"
        animate={isHovered && !props.disabled ? 'animate' : 'initial'}
      >
        {children || 'Join the release waitlist'}
      </motion.span>

      <motion.span
        className="block w-[9.7rem]  flex-shrink-0 origin-left"
        aria-hidden="true"
        variants={rightBarVariants}
        initial="initial"
        animate={isHovered && !props.disabled ? 'animate' : 'initial'}
      >
        <svg
          className="w-full"
          xmlns="http://www.w3.org/2000/svg"
          width="76"
          height="17"
          fill="none"
          viewBox="0 0 76 17"
        >
          <path
            fill="#fff"
            d="m70.301 8.638 5.66.145.006-1.191-5.66.079c-3.657-.012-6.653-3.413-6.842-7.664L62.475 0c-.217 4.255-3.238 7.615-6.894 7.604L.006 7.394 0 8.584l55.576-.013c3.656.011 6.652 3.413 6.841 7.663l.99.008c.217-4.256 3.237-7.616 6.894-7.604"
          ></path>
        </svg>
      </motion.span>
    </button>
  );
});

JoinWaitlistButton.displayName = 'JoinWaitlistButton';

export default JoinWaitlistButton;
